// src/Dashboard/pages/teacher/Belts/components/BeltHistoryDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  FaAward,
  FaEdit,
  FaPlus,
  FaSave,
  FaTimes,
  FaTrash,
} from "react-icons/fa";
import { toast } from "react-toastify";

import {
  addBeltHistory,
  deleteBeltHistory,
  editBeltHistory,
  getBeltHistoryByAdherent,
} from "../../../../../services/beltServices";

import { getAdherentById } from "../../../../../services/adherentServices";

import {
  DEFAULT_BELT_PHOTO,
  DEFAULT_USER_PHOTO,
  dateToInputValue,
  formatDateFr,
  getAdherentGroupsLabel,
  getAdherentName,
  getAdherentPhoto,
  getTodayInputDate,
} from "../utils/beltDashboardUtils";

function getBeltImageFromHistory(history) {
  if (!history?.BeltColor?.belt_url) return DEFAULT_BELT_PHOTO;

  return `/img/belt/${history.BeltColor.belt_url}`;
}

function HistoryForm({
  belts,
  form,
  setForm,
  editingId,
  saving,
  onCancel,
  onSubmit,
}) {
  const sortedBelts = [...belts].sort((a, b) => Number(a.id || 0) - Number(b.id || 0));

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/50"
    >
      <div className="flex flex-col gap-3 sm:grid sm:grid-cols-[1fr_170px_auto] sm:items-end">
        <div>
          <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Ceinture
          </label>

          <select
            value={form.belt_id}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, belt_id: event.target.value }))
            }
            disabled={saving}
            className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          >
            <option value="">Sélectionner une ceinture</option>
            {sortedBelts.map((belt) => (
              <option key={belt.id} value={belt.id}>
                {belt.nom}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Date
          </label>

          <input
            type="date"
            value={form.date_passage}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                date_passage: event.target.value,
              }))
            }
            disabled={saving}
            className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          />
        </div>

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#012f6c] disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />
            {saving
              ? "Enregistrement..."
              : editingId
              ? "Modifier"
              : "Ajouter"}
          </button>

          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          >
            <FaTimes />
          </button>
        </div>
      </div>
    </form>
  );
}

export default function BeltHistoryDrawer({
  isOpen,
  adherent,
  belts = [],
  onClose,
  onSaved,
}) {
  const [adherentDetails, setAdherentDetails] = useState(null);
  const [histories, setHistories] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    belt_id: "",
    date_passage: getTodayInputDate(),
  });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const adherentId = adherent?.id;

  const displayAdherent = adherentDetails || adherent;

  const currentHistory = useMemo(() => {
    return histories?.[0] || null;
  }, [histories]);

  const loadDrawerData = async () => {
    if (!adherentId) return;

    try {
      setLoading(true);

      const [details, historyRows] = await Promise.all([
        getAdherentById(adherentId).catch(() => adherent || null),
        getBeltHistoryByAdherent(adherentId).catch(() => []),
      ]);

      const sorted = (Array.isArray(historyRows) ? historyRows : []).slice().sort((a, b) => {
        const dateA = new Date(a.date_passage).getTime();
        const dateB = new Date(b.date_passage).getTime();

        return dateB - dateA;
      });

      setAdherentDetails(details || adherent || null);
      setHistories(sorted);
    } catch (error) {
      console.error("Erreur chargement historique ceinture :", error);
      toast.error("Erreur lors du chargement de l’historique.");
      setAdherentDetails(adherent || null);
      setHistories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !adherentId) return;

    setShowForm(false);
    setEditingId(null);
    setForm({
      belt_id: "",
      date_passage: getTodayInputDate(),
    });

    loadDrawerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, adherentId]);

  useEffect(() => {
    if (!isOpen) return;

    const root = document.documentElement;
    const previousOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const onEscape = (event) => {
      if (event.key === "Escape" && !saving && !deletingId) {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onEscape);

    return () => {
      root.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onEscape);
    };
  }, [isOpen, saving, deletingId, onClose]);

  const openCreateForm = () => {
    setEditingId(null);
    setForm({
      belt_id: "",
      date_passage: getTodayInputDate(),
    });
    setShowForm(true);
  };

  const openEditForm = (history) => {
    setEditingId(history.id);
    setForm({
      belt_id: history.belt_id ? String(history.belt_id) : "",
      date_passage: dateToInputValue(history.date_passage),
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setEditingId(null);
    setForm({
      belt_id: "",
      date_passage: getTodayInputDate(),
    });
    setShowForm(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!adherentId) {
      toast.error("Adhérent introuvable.");
      return;
    }

    if (!form.belt_id) {
      toast.warn("Sélectionnez une ceinture.");
      return;
    }

    if (!form.date_passage) {
      toast.warn("Choisissez une date.");
      return;
    }

    try {
      setSaving(true);

      if (editingId) {
        await editBeltHistory(editingId, {
          adherent_id: adherentId,
          belt_id: Number(form.belt_id),
          date_passage: form.date_passage,
        });

        toast.success("Passage de grade modifié.");
      } else {
        await addBeltHistory({
          adherent_id: adherentId,
          belt_id: Number(form.belt_id),
          date_passage: form.date_passage,
        });

        toast.success("Passage de grade ajouté.");
      }

      closeForm();
      await loadDrawerData();
      await onSaved?.();
    } catch (error) {
      console.error("Erreur sauvegarde passage :", error);
      toast.error("Erreur lors de l’enregistrement du passage.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (history) => {
    if (!history?.id) return;

    if (!window.confirm("Supprimer ce passage de grade ?")) return;

    try {
      setDeletingId(history.id);

      await deleteBeltHistory(history.id);

      toast.success("Passage de grade supprimé.");
      await loadDrawerData();
      await onSaved?.();
    } catch (error) {
      console.error("Erreur suppression passage :", error);
      toast.error("Erreur lors de la suppression.");
    } finally {
      setDeletingId(null);
    }
  };

  if (!isOpen || !adherent) return null;

  const groupsLabel = getAdherentGroupsLabel(displayAdherent);

  return (
    <div className="fixed inset-0 z-[80]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-4xl flex-col bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Historique de ceinture
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              {getAdherentName(displayAdherent) || "Adhérent"}
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Consultez toute la chronologie, ajoutez un passage ou modifiez une
              ceinture existante.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving || deletingId}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            <FaTimes />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[320px_1fr]">
            <aside className="space-y-4">
              <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="bg-slate-50 p-4 dark:bg-slate-800/40">
                  <div className="flex items-center gap-3">
                    <img
                      src={getAdherentPhoto(displayAdherent)}
                      alt={getAdherentName(displayAdherent)}
                      onError={(event) => {
                        event.currentTarget.src = DEFAULT_USER_PHOTO;
                      }}
                      className="h-16 w-16 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                    />

                    <div className="min-w-0">
                      <p className="truncate font-bold text-slate-900 dark:text-white">
                        {getAdherentName(displayAdherent) || "—"}
                      </p>

                      <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                        Saison {displayAdherent?.saison_active || "—"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 p-4">
                  <div className="rounded-2xl bg-slate-50 p-3 dark:bg-slate-800/60">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Ceinture actuelle
                    </p>

                    <div className="mt-2 flex items-center gap-3">
                      <img
                        src={getBeltImageFromHistory(currentHistory)}
                        alt={currentHistory?.BeltColor?.nom || "Ceinture"}
                        onError={(event) => {
                          event.currentTarget.src = DEFAULT_BELT_PHOTO;
                        }}
                        className="h-8 object-contain"
                      />

                      <div>
                        <p className="font-bold text-slate-900 dark:text-white">
                          {currentHistory?.BeltColor?.nom || "—"}
                        </p>

                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {formatDateFr(currentHistory?.date_passage)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {groupsLabel ? (
                    <div className="rounded-2xl bg-slate-50 p-3 dark:bg-slate-800/60">
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Groupe(s)
                      </p>

                      <p className="mt-1 text-sm leading-6 text-slate-700 dark:text-slate-200">
                        {groupsLabel}
                      </p>
                    </div>
                  ) : null}

                  <button
                    type="button"
                    onClick={openCreateForm}
                    disabled={saving || loading}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#012f6c] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <FaPlus />
                    Ajouter un passage
                  </button>
                </div>
              </div>
            </aside>

            <div className="space-y-4">
              {showForm ? (
                <HistoryForm
                  belts={belts}
                  form={form}
                  setForm={setForm}
                  editingId={editingId}
                  saving={saving}
                  onCancel={closeForm}
                  onSubmit={handleSubmit}
                />
              ) : null}

              <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
                <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-800/40">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Chronologie
                      </p>

                      <h3 className="mt-1 font-bold text-slate-900 dark:text-white">
                        {histories.length} passage
                        {histories.length > 1 ? "s" : ""}
                      </h3>
                    </div>

                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                      <FaAward />
                    </span>
                  </div>
                </div>

                <div className="p-4">
                  {loading ? (
                    <div className="space-y-3">
                      {[...Array(4)].map((_, index) => (
                        <div
                          key={index}
                          className="h-20 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
                        />
                      ))}
                    </div>
                  ) : histories.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center dark:border-slate-700 dark:bg-slate-800/40">
                      <p className="font-bold text-slate-900 dark:text-white">
                        Aucun passage enregistré
                      </p>

                      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        Ajoutez le premier passage de ceinture pour cet adhérent.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {histories.map((history) => {
                        const deleting = deletingId === history.id;

                        return (
                          <article
                            key={history.id}
                            className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900"
                          >
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                              <div className="flex items-center gap-3">
                                <img
                                  src={getBeltImageFromHistory(history)}
                                  alt={history?.BeltColor?.nom || "Ceinture"}
                                  onError={(event) => {
                                    event.currentTarget.src = DEFAULT_BELT_PHOTO;
                                  }}
                                  className="h-9 object-contain"
                                />

                                <div>
                                  <p className="font-bold text-slate-900 dark:text-white">
                                    {history?.BeltColor?.nom || "Ceinture inconnue"}
                                  </p>

                                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                                    Passage du {formatDateFr(history.date_passage)}
                                  </p>
                                </div>
                              </div>

                              <div className="flex justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => openEditForm(history)}
                                  disabled={saving || deleting}
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                                  title="Modifier"
                                >
                                  <FaEdit />
                                </button>

                                <button
                                  type="button"
                                  onClick={() => handleDelete(history)}
                                  disabled={saving || deleting}
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-rose-200 bg-rose-50 text-rose-700 transition hover:bg-rose-100 disabled:opacity-50 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300"
                                  title="Supprimer"
                                >
                                  <FaTrash />
                                </button>
                              </div>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}